/* signup-vid-link.js
 * Ties on-site email-capture surfaces to OUR visitor_id by forwarding
 * {email, vid, source} to the vid-email-resolve sink (-> BigQuery users_drip).
 * Companion to back-in-stock-link.js. Covers:
 *   - signup/marketing FORM submits (newsletter / contact / register + the T&E
 *     Drip form), classified by Shopify hidden `form_type` or a getdrip.com action;
 *   - the LOGGED-IN customer's email (window.AGB_Customer.email), once/session;
 *   - inbound email-campaign CLICKS (?email= in the URL), once/session — this
 *     also catches a known email arriving on a NEW device (a new vid), which the
 *     server-side /save-visitor mirror deliberately skips.
 *
 * Many-to-many by design: a vid can pick up several emails and an email several
 * vids; each distinct (source|email|vid) is sent at most once per session (the
 * sink is append-only, dedupe happens on query). NEVER blocks or alters any
 * form/submit. No-ops until window.AGB_VID_EMAIL_URL is set.
 */
(function () {
  'use strict';

  var ENDPOINT = (typeof window !== 'undefined' && window.AGB_VID_EMAIL_URL) || '';
  if (!ENDPOINT) return;                 // not configured yet -> no-op
  var EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

  // Shopify form_type -> source. ALLOWLIST: anything else is skipped, so
  // customer_login / recover_customer_password / new_comment / search never fire.
  var FORM_TYPE_SOURCE = {
    customer: 'newsletter',              // {% form 'customer' %} signup
    contact: 'contact_form',             // {% form 'contact' %}
    create_customer: 'account_register'  // {% form 'create_customer' %}
  };

  // Pages where ANY on-site form carrying an email should be captured — covers
  // embeds whose action/form_type we don't recognise (e.g. the T&E Drip signup
  // page, whose form may not expose a getdrip.com action attribute).
  var PATH_SOURCE = {
    '/pages/t-e-program-signup': 'tande_signup',
    '/pages/kzero-giveaway': 'giveaway'
  };

  // Drip form id -> source. Keys the APP-RENDERED Drip forms (embedded widget +
  // marketing popup) to clean per-form sources so they don't fall through to the
  // generic 'form_submission'. Same names across ROW/EU/IL — the users_drip
  // `region` column separates the stores. The embedded widget exposes its id via
  // data-drip-embedded-form; the popup via a hidden <input name="form_id"> (and a
  // dfwid-form-<id> element id). Add a store's Drip form ids here as you wire them.
  var DRIP_FORM_SOURCE = {
    '272422654': 'T&E_form',   // embedded Gear-Testers (בוחני הציוד) signup form
    '228350':    'T&E_popup'   // Drip marketing popup — Gear-Testers signup
  };
  function dripFormId(form) {
    try {
      var id = form.getAttribute('data-drip-embedded-form');
      if (id) return String(id).trim();
      var fid = form.querySelector('input[name="form_id"]');
      if (fid && fid.value) return String(fid.value).trim();
      var m = (form.id || '').match(/dfwid-form-(\d+)/);
      if (m) return m[1];
    } catch (e) {}
    return null;
  }

  function visitorId() {
    var v = '';
    try { if (window.SE_visitorIdentity && window.SE_visitorIdentity.visitor_id) v = window.SE_visitorIdentity.visitor_id; } catch (e) {}
    if (!v) { try { v = localStorage.getItem('visitorID') || ''; } catch (e) {} }
    v = String(v || '').trim();
    // Reject known-junk visitor IDs (the legacy '1' bug, or anything too short to
    // be a real UUID) so we never tie an email to a garbage vid.
    if (!v || v === '1' || v.length < 16) return null;
    return v;
  }

  // Track emails captured via a FORM this session, so the post-submit reload
  // (which carries ?email= and would re-fire 'email_click') doesn't double-log.
  function _markFormCaptured(email) { try { sessionStorage.setItem('AGB_fc_' + email, '1'); } catch (e) {} }
  function _formCaptured(email) { try { return sessionStorage.getItem('AGB_fc_' + email) === '1'; } catch (e) { return false; } }

  // Send each distinct (source|email|vid) at most once per session.
  function alreadySent(key) {
    try {
      var k = 'AGB_vidlink_' + key;
      if (sessionStorage.getItem(k)) return true;
      sessionStorage.setItem(k, '1');
      return false;
    } catch (e) { return false; }        // storage blocked -> allow (best-effort)
  }

  function _send(email, source, vid) {
    // SAFETY GATE (session-guard.js). Binds an EMAIL to a VISITOR ID in the
    // append-only identity spine — denied for every non-shopper session.
    try {
      if (window.SE_Session) {
        if (!window.SE_Session.allow('identity', source)) return;
      } else {
        // Guard asset missing (404 / parse error / not yet pushed). An "assumed"
        // session is IMPOSSIBLE without it — begin() lives in the guard — so the
        // legacy prefix check is a COMPLETE substitute here, and a real shopper
        // is never broken by a missing file. Fail-safe, not fail-dark.
        var _gv = ''; try { _gv = localStorage.getItem('visitorID') || ''; } catch (e2) {}
        if (_gv.indexOf('facade00-') === 0 || _gv.indexOf('staff00-') === 0) return;
      }
    } catch (e) { /* never block a real shopper on the guard itself */ }
    if (alreadySent(source + '|' + email + '|' + vid)) return;
    if (source !== 'email_click') _markFormCaptured(email);
    try {
      fetch(ENDPOINT, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: email, vid: vid, source: source }),
        credentials: 'omit',             // backend runs allow_credentials=False
        keepalive: true
      }).catch(function () {});
    } catch (e) { /* fire-and-forget */ }
  }

  // First-touch safety: if a form is submitted before the engine has minted a
  // visitorID (e.g. a giveaway popup on the very first page view), queue the
  // email and flush it the moment a vid is announced — so we never lose the pair.
  var _pending = [];
  var _pendingHooked = false;
  function _flushPending() {
    var vid = visitorId();
    if (!vid) return;
    var q = _pending.splice(0, _pending.length);
    for (var i = 0; i < q.length; i++) _send(q[i].email, q[i].source, vid);
  }

  function forward(email, source) {
    if (!email) return;
    email = String(email).trim().toLowerCase();
    if (!EMAIL_RE.test(email)) return;
    // Skip the lower-value email_click echo if the form already captured this email.
    if (source === 'email_click' && _formCaptured(email)) return;
    var vid = visitorId();
    if (vid) { _send(email, source, vid); return; }
    _pending.push({ email: email, source: source });
    if (!_pendingHooked) {
      _pendingHooked = true;
      window.addEventListener('SE_visitorIdentified', _flushPending);
      setTimeout(_flushPending, 5000);   // safety in case the event already fired
    }
  }

  // ---- 1) signup / marketing FORM submits ----
  function sourceForForm(form) {
    try {
      // Explicit opt-in: any form tagged data-vid-capture="<source>" is captured
      // on ANY page/URL (lets us tie a form regardless of path — incl. a test page).
      var dc = (form.getAttribute('data-vid-capture') || '').trim();
      if (dc) return dc;
      // Drip forms (embedded widget + popup) -> per-form source by Drip form id.
      var dfid = dripFormId(form);
      if (dfid && DRIP_FORM_SOURCE[dfid]) return DRIP_FORM_SOURCE[dfid];
      var action = (form.getAttribute('action') || '').toLowerCase();
      if (action.indexOf('getdrip.com') !== -1) {
        // the warranty-registration page is ALSO a getdrip form — label it distinctly
        if ((location.pathname || '').indexOf('/pages/warranty-registration') === 0) return 'warranty';
        return 'tande_drip_form';
      }
      var ft = form.querySelector('[name="form_type"]');
      var v = (ft && ft.value) ? ft.value.trim() : '';
      if (FORM_TYPE_SOURCE[v]) return FORM_TYPE_SOURCE[v];
      // UpViral giveaway form (INLINE embed only) — its inputs carry id="upv_form_…".
      // (No-op if UpViral is a cross-origin iframe — we can't see into that.)
      if (form.querySelector('[id^="upv_form_"]')) return 'giveaway';
      // page-scoped fallback: an email-bearing form on a known capture page
      // (covers an embed whose action/form_type we don't recognise).
      var ps = PATH_SOURCE[(location.pathname || '').replace(/\/+$/, '')];
      if (ps && form.querySelector('input[type="email"], input[name="fields[email]"], input[name*="email" i]')) return ps;
      // catch-all: ANY other form carrying an email -> generic 'form_submission'.
      // No-email forms (search, localization, add-to-cart) have no email field, so
      // they're skipped automatically. NOTE: this also covers login / password-reset.
      if (form.querySelector('input[type="email"], input[name="fields[email]"], input[name*="email" i]')) return 'form_submission';
      return null;
    } catch (e) { return null; }
  }
  function emailInForm(form) {
    var inp = form.querySelector(
      'input[type="email"], input[name="contact[email]"], input[name="fields[email]"], input[name*="email" i], input[autocomplete="email"]'
    );
    return (inp && inp.value) ? inp.value : null;
  }
  document.addEventListener('submit', function (e) {
    var t = e.target;
    if (!t || t.nodeName !== 'FORM') return;
    var source = sourceForForm(t);
    if (!source) return;
    forward(emailInForm(t), source);
  }, true);

  // ---- 2) logged-in customer email  +  3) inbound ?email= click ----
  function loggedInEmail() {
    try { return (window.AGB_Customer && window.AGB_Customer.email) || null; } catch (e) { return null; }
  }
  function emailFromURL() {
    try {
      // `bde` first: the IL geo-redirect deletes a parameter named "email"
      // outright (measured 2026-09-16). Older links only have `email`.
      var q = new URLSearchParams(window.location.search);
      var e = q.get('bde') || q.get('email');
      return e ? e.replace(/\s/g, '+') : null; // Shopify campaign links encode '+' as space
    } catch (x) { return null; }
  }
  function loadCaptures() {
    forward(loggedInEmail(), 'logged_in');
    forward(emailFromURL(), 'email_click');
  }
  if (document.readyState !== 'loading') loadCaptures();
  else document.addEventListener('DOMContentLoaded', loadCaptures);
  // vid often resolves AFTER load (scarcity engine) -> retry when it announces.
  window.addEventListener('SE_visitorIdentified', loadCaptures);
})();
